import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgeSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    try {
      const res = await axios.get(`${API}/forge/status/${sessionId}`);
      const data = res.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        return;
      }

      if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      if (attempts < 10) {
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } else {
        setStatus("timeout");
      }
    } catch (err) {
      console.error("Forge status error:", err);
      if (attempts < 10) {
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center" data-testid="forge-success-page">
        {status === "loading" && (
          <div data-testid="forge-loading">
            <Loader2 size={48} className="animate-spin text-gold mx-auto mb-6" />
            <h1
              className="text-3xl font-medium text-[#EAEAEA] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Confirming Your Order...
            </h1>
            <p className="text-[#737373]">Verifying payment.</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="forge-confirmed">
            <CheckCircle size={48} className="text-gold mx-auto mb-6" />
            <h1
              className="text-4xl font-medium text-[#EAEAEA] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              The Forge is Ignited
            </h1>
            <p className="text-[#A1A1A1] mb-6 leading-relaxed">
              Your custom beat is now in production. Our producers are crafting your sound.
              You'll receive your exclusive beat and stems via email within 24 hours.
            </p>
            <p className="text-sm text-[#737373] mb-8">
              Questions? Reach us at info@sanctumsonics.com
            </p>
            <Link
              to="/"
              data-testid="forge-back-to-store"
              className="inline-block px-8 py-3 bg-gold text-black font-medium text-sm tracking-widest uppercase rounded-sm hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-all"
            >
              Return to Store
            </Link>
          </div>
        )}

        {(status === "error" || status === "timeout" || status === "expired") && (
          <div data-testid="forge-error">
            <XCircle size={48} className="text-red-400 mx-auto mb-6" />
            <h1
              className="text-3xl font-medium text-[#EAEAEA] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="text-[#737373] mb-6">
              If you were charged, contact info@sanctumsonics.com
            </p>
            <Link
              to="/"
              className="inline-block px-8 py-3 bg-gold text-black font-medium text-sm tracking-widest uppercase rounded-sm"
            >
              Return to Store
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
